import { useAtsConversationApi } from '@/composables/useApi';
import { InternalError } from '@/core/shared/errors/internal.error';
import type {
  CalendarConnectionStatusResponse,
  InitiateOAuthResponse,
  UpdateCalendarEnabledDto,
  UpdateDefaultCalendarDto,
} from './types/calendar.types';

export class CalendarPersistence {
  public async getConnectionStatus(
    userProfileIds: number[],
  ): Promise<CalendarConnectionStatusResponse> {
    try {
      const stringUserProfileIds = userProfileIds.map((id) => String(id));
      const queryParams = new URLSearchParams();
      stringUserProfileIds.forEach((id) => {
        queryParams.append('userProfileIds', id);
      });

      const { data, error, statusCode } = await useAtsConversationApi(
        `/calendar/connection/status?${queryParams.toString()}`,
      )
        .get()
        .json<CalendarConnectionStatusResponse>();

      if (error.value) {
        throw new InternalError('Could not get calendar connection status', {
          error: error.value,
          status: statusCode.value,
          data: { userProfileIds },
        });
      }

      return data.value || [];
    } catch (error) {
      if (error instanceof InternalError) {
        throw error;
      }
      throw new InternalError('Could not get calendar connection status', {
        error,
        data: { userProfileIds },
      });
    }
  }

  public async getInitiateOAuthLink(redirectUrl: string): Promise<InitiateOAuthResponse> {
    const { data, error, statusCode } = await useAtsConversationApi(
      `/calendar/oauth/connect?redirectUrl=${encodeURIComponent(redirectUrl)}`,
    )
      .get()
      .json<InitiateOAuthResponse>();

    if (error.value) {
      throw new InternalError('Could not get OAuth authorization URL', {
        error: error.value,
        status: statusCode.value,
        data: { redirectUrl },
      });
    }

    if (!data.value) {
      throw new InternalError('Empty response received from getInitiateOAuthLink', {
        error: error.value,
        status: statusCode.value,
        data: { redirectUrl },
      });
    }

    return data.value;
  }

  public async disconnectCalendar(): Promise<void> {
    const { error, statusCode } = await useAtsConversationApi('/calendar/connection').delete();

    if (error.value) {
      throw new InternalError('Could not disconnect calendar', {
        error: error.value,
        status: statusCode.value,
      });
    }
  }

  public async updateDefaultCalendar(dto: UpdateDefaultCalendarDto): Promise<void> {
    const { error, statusCode } = await useAtsConversationApi('/calendar/default-calendar').patch(
      dto,
    );

    if (error.value) {
      throw new InternalError('Could not update default calendar', {
        error: error.value,
        status: statusCode.value,
        data: { calendarId: dto.calendarId },
      });
    }
  }

  public async updateCalendarEnabled(dto: UpdateCalendarEnabledDto): Promise<void> {
    const { error, statusCode } = await useAtsConversationApi('/calendar/calendars').patch(dto);

    if (error.value) {
      throw new InternalError('Could not update calendar enabled state', {
        error: error.value,
        status: statusCode.value,
        data: { calendarId: dto.calendarId, enabled: dto.enabled },
      });
    }
  }
}
